import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Checkbox,
  Divider,
  Form,
  Input,
  List,
  Modal,
  Popconfirm,
  Space,
  Spin,
  Tag,
  Typography,
  message
} from 'antd';
import { api } from './api';
import type { Label, TenderStatusText } from './types';

const { Title, Text, Paragraph } = Typography;

const DEFAULT_COLOR = '#d3d3d3';

type TenderTextFormValues = {
  title: string;
  body: string;
  isActive: boolean;
};

function normalizeColorForInput(color: string | null | undefined) {
  if (!color || typeof color !== 'string') {
    return DEFAULT_COLOR;
  }
  const trimmed = color.trim();
  if (!trimmed) {
    return DEFAULT_COLOR;
  }
  if (/^#[0-9a-fA-F]{6}$/.test(trimmed)) {
    return trimmed.toLowerCase();
  }
  if (/^[0-9a-fA-F]{6}$/.test(trimmed)) {
    return `#${trimmed.toLowerCase()}`;
  }
  if (/^#[0-9a-fA-F]{3}$/.test(trimmed)) {
    const hex = trimmed.slice(1).toLowerCase();
    return `#${hex
      .split('')
      .map((char) => char + char)
      .join('')}`;
  }
  if (/^[0-9a-fA-F]{3}$/.test(trimmed)) {
    const hex = trimmed.toLowerCase();
    return `#${hex
      .split('')
      .map((char) => char + char)
      .join('')}`;
  }
  return DEFAULT_COLOR;
}

export default function CustomizationPage() {
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const [labels, setLabels] = useState<Label[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingLabel, setEditingLabel] = useState<Label | null>(null);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [deleteLoadingId, setDeleteLoadingId] = useState<string | null>(null);
  const [tenderForm] = Form.useForm<TenderTextFormValues>();
  const [tenderEditForm] = Form.useForm<TenderTextFormValues>();
  const [tenderTexts, setTenderTexts] = useState<TenderStatusText[]>([]);
  const [tenderLoading, setTenderLoading] = useState(false);
  const [tenderSubmitting, setTenderSubmitting] = useState(false);
  const [tenderEditSubmitting, setTenderEditSubmitting] = useState(false);
  const [tenderDeleteId, setTenderDeleteId] = useState<string | null>(null);
  const [editingTenderText, setEditingTenderText] = useState<TenderStatusText | null>(null);

  const loadLabels = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api('/api/labels');
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Kunne ikke hente labels');
      }
      const data = (await res.json()) as Label[];
      setLabels(data);
    } catch (error: any) {
      console.error('Failed to load labels', error);
      message.error(error?.message || 'Kunne ikke hente labels');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadTenderTexts = useCallback(async () => {
    setTenderLoading(true);
    try {
      const res = await api('/api/tender-texts');
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Kunne ikke hente status-tekster');
      }
      const data = (await res.json()) as TenderStatusText[];
      setTenderTexts(data);
    } catch (error: any) {
      console.error('Failed to load tender status texts', error);
      message.error(error?.message || 'Kunne ikke hente status-tekster');
    } finally {
      setTenderLoading(false);
    }
  }, []);

  useEffect(() => {
    loadLabels();
  }, [loadLabels]);

  useEffect(() => {
    loadTenderTexts();
  }, [loadTenderTexts]);

  useEffect(() => {
    if (editingLabel) {
      editForm.setFieldsValue({
        name: editingLabel.name,
        color: normalizeColorForInput(editingLabel.color),
        hideInSearch: Boolean(editingLabel.hideInSearch)
      });
    } else {
      editForm.resetFields();
    }
  }, [editingLabel, editForm]);

  useEffect(() => {
    if (editingTenderText) {
      tenderEditForm.setFieldsValue({
        title: editingTenderText.title,
        body: editingTenderText.body,
        isActive: Boolean(editingTenderText.isActive)
      });
    } else {
      tenderEditForm.resetFields();
    }
  }, [editingTenderText, tenderEditForm]);

  const sortedLabels = useMemo(
    () => [...labels].sort((a, b) => a.name.localeCompare(b.name, 'da', { sensitivity: 'base' })),
    [labels]
  );

  const handleSubmit = useCallback(async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        name: values.name.trim(),
        color: normalizeColorForInput(values.color),
        hideInSearch: Boolean(values.hideInSearch)
      };
      setSubmitting(true);
      const res = await api('/api/labels', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Kunne ikke oprette label');
      }
      message.success('Label oprettet');
      form.resetFields();
      form.setFieldsValue({ color: DEFAULT_COLOR, hideInSearch: false });
      const created = (await res.json()) as Label;
      setLabels((prev) => [...prev, created]);
      await loadLabels();
    } catch (error: any) {
      if (error?.errorFields) {
        return;
      }
      message.error(error?.message || 'Kunne ikke oprette label');
    } finally {
      setSubmitting(false);
    }
  }, [form, loadLabels]);

  const handleEditSubmit = useCallback(async () => {
    if (!editingLabel) {
      return;
    }
    try {
      const values = await editForm.validateFields();
      const payload = {
        name: values.name.trim(),
        color: normalizeColorForInput(values.color),
        hideInSearch: Boolean(values.hideInSearch)
      };
      setEditSubmitting(true);
      const res = await api(`/api/labels/${editingLabel.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Kunne ikke opdatere label');
      }
      message.success('Label opdateret');
      setEditingLabel(null);
      const updated = (await res.json()) as Label;
      setLabels((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
      await loadLabels();
    } catch (error: any) {
      if (error?.errorFields) {
        return;
      }
      message.error(error?.message || 'Kunne ikke opdatere label');
    } finally {
      setEditSubmitting(false);
    }
  }, [editForm, editingLabel, loadLabels]);

  const handleDelete = useCallback(async (label: Label) => {
    setDeleteLoadingId(label.id);
    try {
      const res = await api(`/api/labels/${label.id}`, {
        method: 'DELETE'
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Kunne ikke slette label');
      }
      message.success('Label slettet');
      setLabels((prev) => prev.filter((item) => item.id !== label.id));
    } catch (error: any) {
      message.error(error?.message || 'Kunne ikke slette label');
    } finally {
      setDeleteLoadingId(null);
    }
  }, []);

  const closeEditModal = useCallback(() => {
    setEditingLabel(null);
  }, []);

  const handleTenderSubmit = useCallback(async () => {
    try {
      const values = await tenderForm.validateFields();
      const payload = {
        title: values.title.trim(),
        body: values.body.trim(),
        isActive: Boolean(values.isActive)
      };
      setTenderSubmitting(true);
      const res = await api('/api/tender-texts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Kunne ikke oprette status-tekst');
      }
      message.success('Status-tekst oprettet');
      tenderForm.resetFields();
      tenderForm.setFieldsValue({ isActive: true });
      await loadTenderTexts();
    } catch (error: any) {
      if (error?.errorFields) {
        return;
      }
      message.error(error?.message || 'Kunne ikke oprette status-tekst');
    } finally {
      setTenderSubmitting(false);
    }
  }, [loadTenderTexts, tenderForm]);

  const handleTenderEditSubmit = useCallback(async () => {
    if (!editingTenderText) {
      return;
    }
    try {
      const values = await tenderEditForm.validateFields();
      const payload = {
        title: values.title.trim(),
        body: values.body.trim(),
        isActive: Boolean(values.isActive)
      };
      setTenderEditSubmitting(true);
      const res = await api(`/api/tender-texts/${editingTenderText.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Kunne ikke opdatere status-tekst');
      }
      message.success('Status-tekst opdateret');
      setEditingTenderText(null);
      await loadTenderTexts();
    } catch (error: any) {
      if (error?.errorFields) {
        return;
      }
      message.error(error?.message || 'Kunne ikke opdatere status-tekst');
    } finally {
      setTenderEditSubmitting(false);
    }
  }, [editingTenderText, loadTenderTexts, tenderEditForm]);

  const handleTenderDelete = useCallback(async (tenderText: TenderStatusText) => {
    setTenderDeleteId(tenderText.id);
    try {
      const res = await api(`/api/tender-texts/${tenderText.id}`, { method: 'DELETE' });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Kunne ikke slette status-tekst');
      }
      message.success('Status-tekst slettet');
      await loadTenderTexts();
    } catch (error: any) {
      message.error(error?.message || 'Kunne ikke slette status-tekst');
    } finally {
      setTenderDeleteId(null);
    }
  }, [loadTenderTexts]);

  const closeTenderEditModal = useCallback(() => {
    setEditingTenderText(null);
  }, []);

  return (
    <>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Card>
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <div>
              <Title level={4} style={{ marginBottom: 0 }}>
                Tilpasning
              </Title>
              <Text type="secondary">
                Opret labels, som du kan tilknytte til leads for bedre organisation.
              </Text>
            </div>
            <Form form={form} layout="vertical" onFinish={handleSubmit} autoComplete="off">
              <Form.Item
                name="name"
                label="Navn"
                rules={[{ required: true, message: 'Angiv et navn til label' }]}
              >
                <Input placeholder="F.eks. Vigtig" maxLength={40} allowClear />
              </Form.Item>
              <Form.Item name="color" label="Farve" initialValue={DEFAULT_COLOR}>
                <Input type="color" style={{ width: 96, padding: 0, height: 38 }} />
              </Form.Item>
              <Form.Item name="hideInSearch" valuePropName="checked" initialValue={false}>
                <Checkbox>Skjul i søgeresultat</Checkbox>
              </Form.Item>
              <Form.Item>
                <Button type="primary" htmlType="submit" loading={submitting}>
                  Opret label
                </Button>
              </Form.Item>
            </Form>
          </Space>
        </Card>

        <Card>
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <div>
              <Title level={5} style={{ marginBottom: 0 }}>
                Eksisterende labels
              </Title>
              <Text type="secondary">
                Dine oprettede labels vises herunder. De kan bruges til at organisere leads.
              </Text>
            </div>
            <Divider style={{ margin: '12px 0' }} />
            {loading ? (
              <div style={{ padding: 32, textAlign: 'center' }}>
                <Spin />
              </div>
            ) : sortedLabels.length === 0 ? (
              <div style={{ padding: 16, textAlign: 'center' }}>
                <Text type="secondary">Ingen labels oprettet endnu.</Text>
              </div>
            ) : (
              <List
                dataSource={sortedLabels}
                renderItem={(item) => {
                  const deleteDisabled = item.leadCount > 0;
                  const deleteButton = (
                    <Button
                      key="delete"
                      type="link"
                      danger
                      disabled={deleteDisabled}
                      loading={deleteLoadingId === item.id}
                    >
                      Slet
                    </Button>
                  );
                  const deleteAction = deleteDisabled ? (
                    deleteButton
                  ) : (
                    <Popconfirm
                      key="delete"
                      title="Slet label?"
                      description="Label kan kun slettes, hvis det ikke bruges."
                      okText="Slet"
                      cancelText="Annuller"
                      okButtonProps={{ danger: true, loading: deleteLoadingId === item.id }}
                      onConfirm={() => handleDelete(item)}
                    >
                      {deleteButton}
                    </Popconfirm>
                  );

                  return (
                    <List.Item
                      actions={[
                        <Button key="edit" type="link" onClick={() => setEditingLabel(item)}>
                          Rediger
                        </Button>,
                        deleteAction
                      ]}
                    >
                      <Space size="large" align="center">
                        <Tag
                          color={item.color || DEFAULT_COLOR}
                          style={{ minWidth: 80, textAlign: 'center', fontWeight: 600 }}
                        >
                          {item.name}
                        </Tag>
                        <div>
                          <div style={{ fontWeight: 500 }}>{item.name}</div>
                          <Text type="secondary">
                            Farve: {item.color}
                            <br />
                            {item.leadCount === 0
                              ? 'Bruges endnu ikke'
                              : item.leadCount === 1
                                ? 'Bruges af 1 lead (kan ikke slettes)'
                                : `Bruges af ${item.leadCount} leads (kan ikke slettes)`}
                            {item.hideInSearch ? (
                              <>
                                <br />Skjuler leads i virksomhedssøgning
                              </>
                            ) : null}
                          </Text>
                        </div>
                      </Space>
                    </List.Item>
                  );
                }}
              />
            )}
          </Space>
        </Card>

        <Card>
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <div>
              <Title level={5} style={{ marginBottom: 0 }}>
                Sagstekster
              </Title>
              <Text type="secondary">
                Teksterne vælges af mæglere, når et lead bliver til en aktiv sag.
              </Text>
            </div>
            <Form
              form={tenderForm}
              layout="vertical"
              onFinish={handleTenderSubmit}
              autoComplete="off"
              initialValues={{ isActive: true }}
            >
              <Form.Item
                name="title"
                label="Titel"
                rules={[{ required: true, message: 'Angiv en titel til status-teksten' }]}
              >
                <Input placeholder="F.eks. Kunde informeret" maxLength={80} allowClear />
              </Form.Item>
              <Form.Item
                name="body"
                label="Tekst"
                rules={[{ required: true, message: 'Teksten må ikke være tom' }]}
              >
                <Input.TextArea
                  rows={3}
                  maxLength={500}
                  showCount
                  placeholder="Denne tekst vises som valg i popup'en"
                />
              </Form.Item>
              <Form.Item name="isActive" valuePropName="checked">
                <Checkbox>Aktiv (tilgængelig ved statusændring)</Checkbox>
              </Form.Item>
              <Form.Item>
                <Button type="primary" htmlType="submit" loading={tenderSubmitting}>
                  Opret status-tekst
                </Button>
              </Form.Item>
            </Form>
            <Divider style={{ margin: '12px 0' }} />
            {tenderLoading ? (
              <div style={{ padding: 32, textAlign: 'center' }}>
                <Spin />
              </div>
            ) : tenderTexts.length === 0 ? (
              <div style={{ padding: 16, textAlign: 'center' }}>
                <Text type="secondary">Ingen status-tekster oprettet endnu.</Text>
              </div>
            ) : (
              <List
                dataSource={tenderTexts}
                renderItem={(item) => {
                  const deleteButton = (
                    <Button
                      key="delete"
                      type="link"
                      danger
                      loading={tenderDeleteId === item.id}
                    >
                      Slet
                    </Button>
                  );
                  return (
                    <List.Item
                      actions={[
                        <Button key="edit" type="link" onClick={() => setEditingTenderText(item)}>
                          Rediger
                        </Button>,
                        <Popconfirm
                          key="delete"
                          title="Slet status-tekst?"
                          okText="Slet"
                          cancelText="Annuller"
                          okButtonProps={{ danger: true, loading: tenderDeleteId === item.id }}
                          onConfirm={() => handleTenderDelete(item)}
                        >
                          {deleteButton}
                        </Popconfirm>
                      ]}
                    >
                      <Space direction="vertical" size={4} style={{ width: '100%' }}>
                        <Space size="large" align="center">
                          <div style={{ fontWeight: 500 }}>{item.title}</div>
                          <Tag color={item.isActive ? 'green' : 'default'}>
                            {item.isActive ? 'Aktiv' : 'Inaktiv'}
                          </Tag>
                        </Space>
                        <Paragraph style={{ marginBottom: 0, whiteSpace: 'pre-line' }}>{item.body}</Paragraph>
                      </Space>
                    </List.Item>
                  );
                }}
              />
            )}
          </Space>
        </Card>
      </Space>

      <Modal
        open={Boolean(editingLabel)}
        title="Rediger label"
        onCancel={closeEditModal}
        onOk={() => editForm.submit()}
        okText="Gem"
        cancelText="Annuller"
        confirmLoading={editSubmitting}
        destroyOnClose
      >
        <Form form={editForm} layout="vertical" onFinish={handleEditSubmit} preserve={false}>
          <Form.Item
            name="name"
            label="Navn"
            rules={[{ required: true, message: 'Angiv et navn til label' }]}
          >
            <Input placeholder="F.eks. Vigtig" maxLength={40} allowClear />
          </Form.Item>
          <Form.Item name="color" label="Farve">
            <Input type="color" style={{ width: 96, padding: 0, height: 38 }} />
          </Form.Item>
          <Form.Item name="hideInSearch" valuePropName="checked">
            <Checkbox>Skjul i søgeresultat</Checkbox>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={Boolean(editingTenderText)}
        title="Rediger status-tekst"
        onCancel={closeTenderEditModal}
        onOk={() => tenderEditForm.submit()}
        okText="Gem"
        cancelText="Annuller"
        confirmLoading={tenderEditSubmitting}
        destroyOnClose
      >
        <Form form={tenderEditForm} layout="vertical" onFinish={handleTenderEditSubmit} preserve={false}>
          <Form.Item
            name="title"
            label="Titel"
            rules={[{ required: true, message: 'Angiv en titel til status-teksten' }]}
          >
            <Input placeholder="F.eks. Kunde informeret" maxLength={80} allowClear />
          </Form.Item>
          <Form.Item
            name="body"
            label="Tekst"
            rules={[{ required: true, message: 'Teksten må ikke være tom' }]}
          >
            <Input.TextArea rows={3} maxLength={500} showCount />
          </Form.Item>
          <Form.Item name="isActive" valuePropName="checked">
            <Checkbox>Aktiv (tilgængelig ved statusændring)</Checkbox>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
